'use client'
import { useEffect, useState, useRef } from 'react'
import {
  injectHideSpCss,
  loadSpConfig,
  loadSpWrapper,
  loadSpQueueConfig,
  loadTcfBundle,
  loadUspApiLocator,
} from './loaders'
import { iab } from './iab'
import { injectScript } from './loaders/utils'
import {
  PlayerTCFStringEvent,
  PlayerTCStringEvent,
  TcData,
} from '@/types/consent'
import { getEventService } from '@/services/events/EventInstance'

export const EV_TC_STRING_READY = 'tcStringReady'
export const EV_PLAYER_TCF_STRING = 'playerTCFString'
export const EV_SHOW_HEROES_TCSTRING = 'showHeroesTcString'

const maxPlayerTimeout = 2000

export const Sourcepoint: React.FC = () => {
  const bus = getEventService()

  const [acReady, setAcReady] = useState(false)
  const [tcString, setTcString] = useState<string>('')
  const [forceOverlay, setForceOverlay] = useState(false)

  const skipFirstHide = useRef(true)
  const aliveRef = useRef(true)
  const tcfListenerIdRef = useRef<number | null>(null)

  const onShowPc = () => setForceOverlay(true)

  useEffect(() => {
    aliveRef.current = true

    async function init() {
      loadSpConfig()
      loadSpQueueConfig()
      const wrapper = loadSpWrapper()
      loadTcfBundle()
      loadUspApiLocator()
      injectScript({ id: 'iab-script', content: iab, type: 'text/javascript' })

      if (wrapper) {
        await new Promise<void>((resolve) => {
          wrapper.onload = () => aliveRef.current && resolve()
        })
      }
      if (!aliveRef.current) return
      ;(window as any).processConsent = onConsentReady
      window.__tcfapi?.(
        'addEventListener',
        2,
        (_tcData: TcData, _success: boolean) => {
          if (!aliveRef.current) return
        },
        undefined,
      )

      window.__tcfapi?.(
        'addEventListener',
        2,
        (tcData: TcData, success: boolean, listenerId?: number) => {
          if (!aliveRef.current) return
          if (listenerId != null && tcfListenerIdRef.current == null) {
            tcfListenerIdRef.current = listenerId
          }
          onConsentReady(tcData, success)
        },
      )

      window.__tcfapi?.(
        'getTCData',
        2,
        (tcData?: TcData, success?: boolean) => {
          if (!aliveRef.current || !tcData) return
          onConsentReady(tcData, !!success)
        },
      )

      window.addEventListener('AC_player_start', onPlayerStart)
      window.addEventListener('AC_show_pc', onShowPc)

      window.setTimeout(() => {
        if (!aliveRef.current) return
        triggerPlayer()
      }, maxPlayerTimeout)
    }

    init()

    return () => {
      aliveRef.current = false
      window.removeEventListener('AC_player_start', onPlayerStart)
      window.removeEventListener('AC_show_pc', onShowPc)

      if (tcfListenerIdRef.current != null) {
        const id = tcfListenerIdRef.current
        window.__tcfapi?.('removeEventListener', 2, () => {}, id)
        tcfListenerIdRef.current = null
      } else {
        window.__tcfapi?.('removeEventListener', 2, onConsentReady as any)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (skipFirstHide.current) {
      skipFirstHide.current = false
      return
    }
    if (forceOverlay) {
      document.getElementById('hide-sp-css')?.remove()
    } else {
      injectHideSpCss()
    }
  }, [forceOverlay])

  function onConsentReady(tcData: TcData, success: boolean) {
    if (success) performance.mark('tcf-' + tcData.eventStatus)

    const raw = tcData.tcString ?? ''
    setTcString(raw)

    bus.emit(EV_TC_STRING_READY, {
      tcString: raw,
      tcfString: `consent=${raw}`,
    })

    const core = [1, 2, 3, 4, 7, 8, 9, 10]
    const allCoreConsented = core.every((p) => tcData.purpose?.consents?.[p])
    if (allCoreConsented) {
      const ev = new Event(EV_SHOW_HEROES_TCSTRING) as PlayerTCStringEvent
      ev.tcString = raw
      window.dispatchEvent(ev)
      bus.emit(EV_SHOW_HEROES_TCSTRING, { tcString: raw })
    }

    if (acReady) triggerPlayer()
  }

  function onPlayerStart() {
    setAcReady(true)
    if (tcString) triggerPlayer()
  }

  function triggerPlayer() {
    const ev = new Event(EV_PLAYER_TCF_STRING) as PlayerTCFStringEvent
    ev.tcfString = tcString
    window.dispatchEvent(ev)

    bus.emit(EV_PLAYER_TCF_STRING, {
      tcString,
      tcfString: `consent=${tcString}`,
    })
  }

  return <div id="root" />
}
